import type { ReadFailure, ReadFailureCategory } from '../runtime/read-failure';
import type { PanelRuntimeSession } from '../runtime/session-model';
import { navigationRoleLabel, navigationScope, visibleDestinations } from './access';

// The Panel's own sentences for what the transport and the session know: the runtime carries facts and categories,
// and the words live here, on the presentation side.

/** Support needs the raw fact; the person reading the banner does not. */
export function refusalSupportNote(status: number | null, code: string | null): string {
  const parts = [status === null ? null : `error ${status}`, code].filter((part): part is string => part !== null);
  return parts.length === 0 ? '' : ` (para soporte: ${parts.join(' · ')})`;
}

const READ_FAILURE_MESSAGES: Readonly<Record<ReadFailureCategory, string>> = Object.freeze({
  timeout: 'La consulta tardó demasiado.',
  network: 'No se pudo conectar con el servidor.',
  validation: 'La respuesta del servidor no es válida.',
  session_verification: 'Estamos confirmando tu sesión.',
  denied: 'No tienes acceso a estos datos.',
  expired: 'Tu sesión ha caducado.',
  unavailable: 'El servidor no está disponible ahora.',
  refusal: 'El servidor rechazó la consulta.',
});

/** The short sentence a failed region shows; a server answer adds its support note. */
export function readFailureMessage(read: ReadFailure): string {
  const message = READ_FAILURE_MESSAGES[read.category];
  if (read.provenance === 'local' || read.category === 'validation') return message;
  const note = refusalSupportNote(read.status, read.refusalCode);
  return note ? `${message.slice(0, -1)}${note}.` : message;
}

/** The one role label of a session, derived from one fact:
 * the tenant kind and the session's navigation role (for «Mi cuenta», a
 * non-manager's placements). `account` is the longer
 * wording v20 draws inside «Mi cuenta» (census `configuration.md`); `chrome`
 * is the identity chrome's. Presentation only: the label never admits. */
export function sessionRoleLabel(session: Pick<PanelRuntimeSession, 'tenant'>, form: 'chrome' | 'account' = 'chrome'): string {
  const { tenant } = session;
  if (tenant.kind === 'franchise') return 'Dirección de la franquicia';
  if (tenant.kind === 'platform') return form === 'account' ? 'Superadministración de Exylon' : 'Superadministración';
  const role = tenant.access.navigationRole;
  if (form === 'account') {
    if (role === 'gerencia') return 'Gerente · responsable del local';
    // A non-manager is described by their placements, the destinations they are listed
    // (docs/decisions/2026-11-04-staff-navigation-from-placement.md): «Cocina», «Servicio · TPV · Cocina», «Inventario».
    const placed = navigationScope(visibleDestinations(tenant.access), role).map(({ shortLabel }) => shortLabel);
    return placed.length > 0 ? placed.join(' · ') : 'Sin puesto';
  }
  return role === 'gerencia' ? 'Gerente' : navigationRoleLabel(role);
}
